// src/components/AboutSection.jsx
import React from 'react';
import { Element } from 'react-scroll';
import { motion } from 'framer-motion';
import { Dices, Code, Rocket } from 'lucide-react';
import HowItWorks from './HowItWorks';

export default function AboutSection() {
  return (
    <Element name="about">
      <section className="w-full text-white bg-gradient-to-b from-[#40033e] via-[#2d0028] to-[#40033e] relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 z-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-pink-500/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>

        {/* ORIGIN STORY */}
        <div className="relative min-h-screen flex flex-col items-center justify-center px-4 md:px-12 py-32">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-6xl font-bold font-rubik drop-shadow-xl mb-4">
              The Origin
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-pink-500 to-purple-500 mx-auto rounded-full" />
          </motion.div>

          {/* Story Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="max-w-4xl w-full"
          >
            <div className="relative overflow-hidden bg-white/5 backdrop-blur-md rounded-3xl border border-white/10 p-8 md:p-12 shadow-2xl">
              {/* Gradient glow */}
              <div className="absolute -inset-1 bg-gradient-to-r from-pink-500/20 to-purple-500/20 blur-2xl opacity-50" />

              <div className="relative space-y-6">
                <p className="text-lg md:text-xl text-white/90 leading-relaxed">
                  Shut The Box isn't some new idea cooked up in a boardroom. It's an old pub game, the kind you'd find tucked away in the corner of a seaside bar or buried in a dusty family game box. <span className="text-pink-300 font-semibold">Dice, wood, strategy, and a little bit of nerve</span> — simple and timeless.
                </p>

                <p className="text-lg md:text-xl text-white/90 leading-relaxed">
                  Fast forward to today. I'm in-between gigs <span className="text-white/60 italic">(most call this jobless)</span>, itching to make something real, when I decided to bring this classic onto Binance chain. <span className="text-yellow-300 font-semibold">No big team, no VC money</span> — just me, some code, and the hope of building something worth playing.
                </p>

                <p className="text-lg md:text-xl text-white/90 leading-relaxed">
                  Now it's on-chain, ready for anyone who remembers the thrill of shutting that last tile… or is about to feel it for the first time.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Feature Pills */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16 w-full max-w-4xl"
          >
            <div className="relative group">
              <div className="relative overflow-hidden bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-6 text-center transition-all duration-300 hover:border-pink-400/50 hover:scale-105">
                <div className="flex justify-center mb-4">
                  <div className="p-3 bg-pink-500/20 rounded-full">
                    <Dices className="w-8 h-8 text-pink-400" />
                  </div>
                </div>
                <h3 className="text-lg font-bold mb-2">Classic Game</h3>
                <p className="text-sm text-white/70">Timeless pub game brought to Web3</p>
                <div className="absolute inset-0 bg-gradient-to-br from-pink-500/0 to-pink-500/0 group-hover:from-pink-500/10 group-hover:to-transparent rounded-2xl transition-all duration-300" />
              </div>
            </div>

            <div className="relative group">
              <div className="relative overflow-hidden bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-6 text-center transition-all duration-300 hover:border-purple-400/50 hover:scale-105">
                <div className="flex justify-center mb-4">
                  <div className="p-3 bg-purple-500/20 rounded-full">
                    <Code className="w-8 h-8 text-purple-400" />
                  </div>
                </div>
                <h3 className="text-lg font-bold mb-2">Solo Dev</h3>
                <p className="text-sm text-white/70">Built by one person, for everyone</p>
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/0 to-purple-500/0 group-hover:from-purple-500/10 group-hover:to-transparent rounded-2xl transition-all duration-300" />
              </div>
            </div>

            <div className="relative group">
              <div className="relative overflow-hidden bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-6 text-center transition-all duration-300 hover:border-yellow-400/50 hover:scale-105">
                <div className="flex justify-center mb-4">
                  <div className="p-3 bg-yellow-500/20 rounded-full">
                    <Rocket className="w-8 h-8 text-yellow-400" />
                  </div>
                </div>
                <h3 className="text-lg font-bold mb-2">On-Chain</h3>
                <p className="text-sm text-white/70">Fully transparent, BNB Chain</p>
                <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/0 to-yellow-500/0 group-hover:from-yellow-500/10 group-hover:to-transparent rounded-2xl transition-all duration-300" />
              </div>
            </div>
          </motion.div>
        </div>

        {/* HOW IT WORKS */}
        <HowItWorks />
      </section>
    </Element>
  );
}
